import { type ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react'
import { HelpCircle } from 'lucide-react'
import { createPortal } from 'react-dom'

interface InfoTooltipProps {
  text: ReactNode
  className?: string
}

/** Distance kept between the bubble and the viewport edge, and between the
 *  bubble and its trigger. */
const EDGE = 8
const GAP = 8
const MAX_W = 320

/**
 * Hover help. Portalled to <body> so it escapes any `overflow: hidden` ancestor,
 * and positioned in VIEWPORT coordinates.
 *
 * The bubble is `position: fixed`, which is viewport-relative — so the trigger's
 * getBoundingClientRect() values are used as-is. Adding window.scrollY/scrollX
 * (as this did until 2026-08-18) pushes the bubble down and right by the exact
 * scroll offset, so it reads fine at the top of a page and drifts further off
 * the further you scroll: at ~900px down it landed ~790px low, entirely below
 * the fold. That is the "tooltip appears under the text" report.
 *
 * It also flips below the trigger when there is no room above, and clamps
 * horizontally, so a tooltip on the first row or at either edge stays readable.
 */
export function InfoTooltip({ text, className }: InfoTooltipProps) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLSpanElement>(null)
  const bubbleRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number; below: boolean } | null>(null)

  const place = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const r = trigger.getBoundingClientRect()
    const h = bubbleRef.current?.offsetHeight ?? 0
    const w = Math.min(bubbleRef.current?.offsetWidth || MAX_W, MAX_W)

    // Prefer above; flip below only when the bubble would clip the top.
    const below = r.top - h - GAP < EDGE
    const top = below ? r.bottom + GAP : r.top - GAP

    // Centre on the trigger, then keep the whole bubble on screen.
    const half = w / 2
    const left = Math.min(
      Math.max(r.left + r.width / 2, EDGE + half),
      window.innerWidth - EDGE - half,
    )
    setPos({ top, left, below })
  }, [])

  // Layout effect, not effect: measure and place before paint so the bubble
  // never shows for a frame at the wrong spot.
  useLayoutEffect(() => {
    if (!open) return
    place()
    // Scrolling or resizing while open would strand a fixed bubble beside a
    // trigger that has moved, so track both. Capture phase catches scrolls in
    // any nested scroll container, not just the window.
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, place])

  return (
    <>
      <span
        ref={triggerRef}
        className={`inline-flex ml-1 cursor-help ${className || ''}`}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        tabIndex={0}
        role="button"
        aria-label="More information"
      >
        <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/60 hover:text-muted-foreground transition-colors" />
      </span>
      {open && createPortal(
        <div
          ref={bubbleRef}
          role="tooltip"
          className="pointer-events-none fixed z-[9999]"
          style={{
            top: pos?.top ?? -9999,
            left: pos?.left ?? -9999,
            transform: `translate(-50%, ${pos?.below ? '0' : '-100%'})`,
            // Hidden until measured, so the first paint is never in the wrong place.
            visibility: pos ? 'visible' : 'hidden',
          }}
        >
          <div className="max-w-[320px] rounded-lg bg-gray-900 px-3.5 py-2.5 text-xs leading-relaxed text-gray-100 shadow-lg">
            {typeof text === 'string' ? <p>{text}</p> : text}
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}
